import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Download, CheckCircle2 } from "lucide-react";
import { productBySlug, PRODUCTS } from "../data/products";
import { COMPANY } from "../data/site";
import EnquiryForm from "../components/EnquiryForm";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const p = productBySlug(slug);

  useEffect(() => {
    if (p) document.title = `${p.name} — Weldmatix Fusion`;
  }, [p]);

  if (!p) return <Navigate to="/products" replace />;

  const idx = PRODUCTS.findIndex((x) => x.slug === slug);
  const next = PRODUCTS[(idx + 1) % PRODUCTS.length];
  const heroImg = p.hero || p.image;
  const secondaryImg = p.image_secondary && p.image_secondary !== heroImg ? p.image_secondary : heroImg;

  return (
    <div data-testid={`page-product-${p.slug}`} className="bg-white pt-28 md:pt-36">
      {/* ─── 1. TITLE BLOCK ────────────────────────────────────────────── */}
      <section className="border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-6 pb-12 md:pb-16">
          <Link to="/products" className="inline-flex items-center gap-2 font-mono-wm text-[11px] tracking-[0.25em] uppercase text-[#64748B] hover:text-slate-900 mb-10">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to systems
          </Link>

          <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-end">
            <div className="md:col-span-8">
              <div className="eyebrow mb-3">// {p.short}</div>
              <h1 className="font-display uppercase tracking-tight text-slate-900 text-4xl sm:text-5xl md:text-6xl lg:text-[68px] leading-[0.96]">
                {p.name.split(" ").slice(0, -2).join(" ")}{" "}
                <span className="text-[#0EA5E9]">{p.name.split(" ").slice(-2).join(" ")}</span>
              </h1>
              <p className="mt-5 max-w-2xl text-base md:text-lg text-[#475569] leading-relaxed">{p.tagline}</p>
            </div>

            <div className="md:col-span-4 flex md:justify-end">
              <div className="flex flex-wrap gap-3">
                <a href="#enquire" data-testid="product-cta-enquire"
                   className="group inline-flex items-center gap-3 bg-[#0EA5E9] hover:bg-[#0284C7] text-white px-5 py-3 text-[13px] uppercase tracking-[0.2em]">
                  Request Specs
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition" />
                </a>
                <a href={COMPANY.brochure_url} target="_blank" rel="noopener noreferrer" download
                   data-testid="product-cta-brochure"
                   className="inline-flex items-center gap-3 border border-[#CBD5E1] hover:border-slate-900 text-slate-900 px-5 py-3 text-[13px] uppercase tracking-[0.2em]">
                  Brochure <Download className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── 2. HERO IMAGE — large, dominant ──────────────────────────── */}
      <section className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 md:py-16">
          <figure
            className="relative w-full bg-white border border-[#E2E8F0] shadow-[0_30px_80px_-32px_rgba(15,23,42,0.18)] overflow-hidden"
            data-testid="product-hero-figure"
          >
            <div className="aspect-[16/9] w-full">
              <img
                src={heroImg}
                alt={`${p.name} — primary view`}
                data-testid="product-hero-image"
                className="w-full h-full object-contain"
                loading="eager"
                decoding="async"
              />
            </div>
            {/* Caption strip */}
            <figcaption className="flex items-center justify-between gap-4 border-t border-[#E2E8F0] bg-white px-5 md:px-8 py-3">
              <span className="font-mono-wm text-[10px] tracking-[0.3em] uppercase text-[#0EA5E9]">
                {p.short}
              </span>
              <span className="font-mono-wm text-[10px] tracking-[0.3em] uppercase text-[#64748B] hidden sm:block">
                Weldmatix Fusion · Engineered in-house
              </span>
            </figcaption>
          </figure>
        </div>
      </section>

      {/* ─── 3. OVERVIEW — quiet text section ─────────────────────────── */}
      <section className="border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 md:py-24 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="eyebrow mb-3">// Overview</div>
            <h2 className="font-display uppercase text-slate-900 text-3xl md:text-4xl tracking-tight leading-[0.98]">
              System Summary
            </h2>
          </div>
          <p className="md:col-span-8 text-base md:text-lg text-[#475569] leading-[1.75]">
            {p.description}
          </p>
        </div>
      </section>

      {/* ─── 4. FEATURES + SECONDARY IMAGE + SPECIFICATIONS ───────────── */}
      <section className="border-b border-[#E2E8F0] bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 md:py-24">
          <div className="grid md:grid-cols-12 gap-10 lg:gap-14">
            {/* LEFT — Features + Specifications stacked */}
            <div className="md:col-span-7 space-y-14">
              <div>
                <div className="eyebrow mb-6">// Features</div>
                <ul className="space-y-3.5">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-[15px] text-[#0F172A]">
                      <CheckCircle2 className="w-4 h-4 text-[#0EA5E9] mt-1 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <div className="eyebrow mb-6">// Specifications</div>
                <div className="border border-[#E2E8F0] divide-y divide-[#F1F5F9]">
                  {p.specs.map((s) => (
                    <div key={s.k} className="grid grid-cols-12 py-3.5 px-4 md:px-5 text-sm">
                      <div className="col-span-5 font-mono-wm text-[11px] tracking-[0.2em] uppercase text-[#64748B] self-center">
                        {s.k}
                      </div>
                      <div className="col-span-7 text-slate-900 font-mono-wm text-[13px]">
                        {s.v}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* RIGHT — Secondary image, ~40% of hero width, naturally aligned */}
            <aside className="md:col-span-5">
              <div className="md:sticky md:top-32">
                <figure className="bg-white border border-[#E2E8F0] shadow-[0_18px_48px_-24px_rgba(15,23,42,0.15)] overflow-hidden">
                  <div className="aspect-[4/3] bg-[#F8FAFC]">
                    <img
                      src={secondaryImg}
                      alt={`${p.name} — supporting view`}
                      data-testid="product-secondary-image"
                      className="w-full h-full object-contain"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <figcaption className="px-5 py-3 border-t border-[#E2E8F0] flex items-center justify-between">
                    <span className="font-mono-wm text-[10px] tracking-[0.3em] uppercase text-[#64748B]">
                      Reference view
                    </span>
                    <span className="font-mono-wm text-[10px] tracking-[0.3em] uppercase text-[#0EA5E9]">
                      // 02
                    </span>
                  </figcaption>
                </figure>

                {/* Inline contextual CTA — keeps the column purposeful */}
                <div className="mt-6 hidden md:block">
                  <a
                    href="#enquire"
                    className="group inline-flex items-center gap-2 text-[13px] uppercase tracking-[0.2em] text-[#0EA5E9] hover:text-[#0284C7]"
                    data-testid="product-inline-enquire"
                  >
                    Request technical drawings
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* ─── 5. APPLICATIONS / INDUSTRIES / ADVANTAGES ────────────────── */}
      <section className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            {[
              { t: "Applications", items: p.applications },
              { t: "Industries", items: p.industries },
              { t: "Advantages", items: p.advantages }
            ].map((b) => (
              <div key={b.t} className="bg-white p-8 md:p-10">
                <div className="eyebrow mb-6">// {b.t}</div>
                <ul className="space-y-3">
                  {b.items.map((i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-[#0F172A]">
                      <span className="w-2 h-2 bg-[#0EA5E9] mt-1.5 flex-shrink-0" />
                      <span>{i}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── 6. ENQUIRY ───────────────────────────────────────────────── */}
      <section id="enquire" className="border-b border-[#E2E8F0] bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 md:py-24 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="eyebrow mb-4">// Enquire</div>
            <h2 className="font-display uppercase text-slate-900 text-3xl md:text-5xl tracking-tight leading-[0.95]">
              Request specs for<br /><span className="text-[#0EA5E9]">{p.short}</span>
            </h2>
            <p className="mt-4 text-[#475569] leading-relaxed">
              Share your application requirements. Our engineering team will respond with
              a detailed configuration, layout drawings and indicative pricing.
            </p>
          </div>
          <div className="md:col-span-7">
            <EnquiryForm defaultProduct={p.name} />
          </div>
        </div>
      </section>

      {/* ─── 7. ALL PRODUCTS CAROUSEL ─────────────────────────────────── */}
      <section data-testid="product-rail" className="border-t border-[#E2E8F0] bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-12 md:pt-16 pb-4">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="eyebrow mb-2">// All Systems</div>
              <h3 className="font-display uppercase text-slate-900 text-2xl md:text-3xl tracking-tight leading-tight">
                Explore the full Weldmatix range
              </h3>
            </div>
            <Link
              to="/products"
              data-testid="product-rail-view-all"
              className="hidden md:inline-flex items-center gap-2 font-mono-wm text-xs tracking-[0.2em] uppercase text-[#0EA5E9] hover:text-[#0284C7]"
            >
              View all <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="relative">
          <div
            data-testid="product-rail-track"
            className="flex gap-4 md:gap-5 overflow-x-auto snap-x snap-mandatory px-6 lg:px-12 pb-12 scroll-smooth [scrollbar-width:thin]"
          >
            {PRODUCTS.map((rp) => {
              const isActive = rp.slug === p.slug;
              return (
                <Link
                  key={rp.slug}
                  to={`/products/${rp.slug}`}
                  data-testid={`product-rail-card-${rp.slug}`}
                  className={`group relative flex-shrink-0 w-[240px] md:w-[260px] snap-start bg-white border transition-all duration-200 ${
                    isActive
                      ? "border-[#0EA5E9] shadow-[0_18px_40px_-20px_rgba(14,165,233,0.45)]"
                      : "border-[#E2E8F0] hover:border-[#0EA5E9] hover:-translate-y-1 hover:shadow-[0_14px_32px_-16px_rgba(15,23,42,0.18)]"
                  }`}
                >
                  <div className="aspect-[4/3] bg-[#F8FAFC] overflow-hidden">
                    <img
                      src={rp.image}
                      alt={rp.name}
                      loading="lazy"
                      className="w-full h-full object-contain p-3 group-hover:scale-[1.03] transition-transform duration-300"
                    />
                  </div>
                  <div className="px-4 py-3 border-t border-[#F1F5F9]">
                    <div className={`font-mono-wm text-[10px] tracking-[0.25em] uppercase mb-1 ${isActive ? "text-[#0EA5E9]" : "text-[#64748B]"}`}>
                      {isActive ? "// Current" : rp.short.split("·")[0].trim()}
                    </div>
                    <div className="font-display text-[14px] uppercase tracking-tight text-[#0F172A] leading-tight line-clamp-2">
                      {rp.name}
                    </div>
                  </div>
                  {isActive && (
                    <span className="absolute top-3 right-3 w-2 h-2 bg-[#0EA5E9] rounded-full" aria-hidden="true" />
                  )}
                </Link>
              );
            })}
          </div>
        </div>

        {/* Up next single-line strip */}
        <div className="border-t border-[#E2E8F0] bg-white">
          <Link
            to={`/products/${next.slug}`}
            data-testid="product-next"
            className="group block py-8"
          >
            <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between gap-6">
              <div className="flex items-center gap-5">
                <div className="font-mono-wm text-[10px] tracking-[0.3em] uppercase text-[#64748B]">Up next ›</div>
                <h4 className="font-display uppercase text-slate-900 group-hover:text-[#0EA5E9] text-xl md:text-2xl tracking-tight transition-colors">
                  {next.name}
                </h4>
              </div>
              <ArrowUpRight className="w-6 h-6 md:w-8 md:h-8 text-slate-900 group-hover:text-[#0EA5E9] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
}
